import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users,
    Briefcase,
    Plus,
    Edit,
    Trash2,
    Eye,
    EyeOff,
    MapPin,
    Clock,
    DollarSign,
    Calendar,
    X,
    Save,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    Grid3x3,
    List,
    RotateCcw,
    Sparkles,
    Loader2
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { AdminLayout } from './components/AdminLayout';
import { AdminTopBar } from './components/AdminTopBar';
import { SearchAndFilters } from './components/SearchAndFilters';
import { BackLink } from './components/BackLink';
import { EmptyState } from './components/EmptyState';
import { Pagination } from './components/Pagination';
import { useJobs, Job as JobType } from '../../contexts/JobsContext';
import { JobExpirationCountdown } from '../../JobExpirationCountdown';
import { formatPostedDate } from '../../../types/job';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { careersAPI, aiAPI } from '../../../services/api';
import { useSocketEvent } from '../../contexts/SocketContext';

// Predefined options for job posting
const JOB_TITLES = [
    'UI/UX Designer – AI Products',
    'Full Stack Developer – Enterprise AI',
    'Machine Learning Engineer – Industrial AI',
    'Data Analyst – AI Insights',
    'Project Manager – AI Solutions',
    'Senior AI Engineer',
    'AI Research Scientist',
    'DevOps Engineer',
    'Data Scientist',
    'Product Manager',
    'Business Analyst',
    'Frontend Developer',
    'Backend Developer',
    'Cloud Architect',
    'QA Engineer',
    'Technical Writer',
    'Other'
];

const DEPARTMENTS = [
    'Engineering',
    'Design',
    'Product',
    'Research',
    'Data & Analytics',
    'Infrastructure',
    'Sales & Marketing',
    'Human Resources',
    'Operations',
    'Finance',
    'Customer Success'
];

const LOCATIONS = [
    'Chennai, India',
    'Erode, India',
    'Bangalore, India',
    'Hyderabad, India',
    'Mumbai, India',
    'Delhi NCR, India',
    'Pune, India',
    'Remote',
    'Hybrid',
    'On-site'
];

// Use the Job type from JobsContext
type Job = JobType;

export function AdminCareers() {
    const { jobs, addJob, updateJob, deleteJob, refreshJobs, isLoading } = useJobs();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [departmentFilter, setDepartmentFilter] = useState<string>('all');
    const [showJobForm, setShowJobForm] = useState(false);
    const [editingJob, setEditingJob] = useState<Job | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
    const [selectedJob, setSelectedJob] = useState<Job | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const itemsPerPage = 5;
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        itemId: '',
        itemName: '',
        isDeleting: false
    });

    // Get current user from localStorage to check if super_admin
    const currentUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    const isSuperAdmin = currentUser?.role === 'super_admin';

    // Real-time socket event handlers for careers
    useSocketEvent('career:created', useCallback(() => {
        refreshJobs();
        toast.info('New job posting added!');
    }, [refreshJobs]));

    useSocketEvent('career:updated', useCallback(() => {
        refreshJobs();
        toast.info('Job posting updated');
    }, [refreshJobs]));

    useSocketEvent('career:deleted', useCallback(() => {
        refreshJobs();
        toast.info('Job posting removed');
    }, [refreshJobs]));

    const [formData, setFormData] = useState<Partial<Job>>({
        title: '',
        department: '',
        location: '',
        workMode: '',
        type: 'Full-time',
        experience: '',
        salary: '',
        description: '',
        jobSummary: '',
        requirements: [],
        responsibilities: [],
        preferredQualifications: [],
        benefits: [],
        teamInfo: '',
        status: 'draft',
        relocationSupport: false
    });

    const [requirementInput, setRequirementInput] = useState('');
    const [responsibilityInput, setResponsibilityInput] = useState('');
    const [benefitInput, setBenefitInput] = useState('');
    const [preferredQualificationInput, setPreferredQualificationInput] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    const toggleExpanded = (jobId: string) => {
        setExpandedJobId(expandedJobId === jobId ? null : jobId);
    };

    const handleViewMore = (job: Job) => {
        setSelectedJob(job);
        setShowDetailModal(true);
    };

    const handleAddJob = () => {
        setEditingJob(null);
        setFormData({
            title: '',
            department: '',
            location: '',
            workMode: '',
            type: 'Full-time',
            experience: '',
            salary: '',
            description: '',
            jobSummary: '',
            requirements: [],
            responsibilities: [],
            preferredQualifications: [],
            benefits: [],
            teamInfo: '',
            status: 'draft',
            relocationSupport: false,
            expirationDate: '',
            postedDate: ''
        });
        setShowJobForm(true);
    };

    const handleEditJob = (job: Job) => {
        setEditingJob(job);
        // Ensure all fields have default values to prevent controlled/uncontrolled errors
        setFormData({
            title: job.title || '',
            department: job.department || '',
            location: job.location || '',
            workMode: job.workMode || '',
            type: job.type || 'Full-time',
            experience: job.experience || '',
            salary: job.salary || '',
            description: job.description || '',
            jobSummary: job.jobSummary || '',
            requirements: job.requirements || [],
            responsibilities: job.responsibilities || [],
            preferredQualifications: job.preferredQualifications || [],
            benefits: job.benefits || [],
            teamInfo: job.teamInfo || '',
            status: job.status || 'draft',
            applicationLink: job.applicationLink || '',
            relocationSupport: job.relocationSupport || false,
            expirationDate: job.expirationDate || '',
            postedDate: job.postedDate || ''
        });
        setShowJobForm(true);
    };

    const handleAIGenerate = async () => {
        if (!aiPrompt.trim()) {
            toast.error('Please enter a prompt for AI job generation');
            return;
        }

        setIsGenerating(true);
        try {
            toast.info('Generating job description with AI...');

            // Prepare the prompt - if we have existing data, include it for context
            let finalPrompt = aiPrompt;
            const hasExistingData = formData.title || formData.description || (formData.responsibilities?.length ?? 0) > 0;

            if (hasExistingData) {
                // Include current job data for context when editing
                const currentJobContext = {
                    title: formData.title,
                    department: formData.department,
                    location: formData.location,
                    experience: formData.experience,
                    responsibilities: formData.responsibilities?.slice(0, 3), // Include first 3 responsibilities
                    requirements: formData.requirements?.slice(0, 3), // Include first 3 requirements
                };

                finalPrompt = `Current job: ${JSON.stringify(currentJobContext)}\n\nUser request: ${aiPrompt}`;
            }

            const response = await aiAPI.generateJobDescription(finalPrompt);

            if (response.success && response.data) {
                // Clear all existing form data and populate with AI-generated data
                setFormData({
                    title: response.data.title || '',
                    department: response.data.department || '',
                    location: response.data.location || '',
                    workMode: response.data.workMode || '',
                    type: response.data.type || 'Full-time',
                    experience: response.data.experience || '',
                    salary: response.data.salary || '',
                    description: response.data.description || '',
                    jobSummary: response.data.jobSummary || '',
                    responsibilities: response.data.responsibilities || [],
                    requirements: response.data.requirements || [],
                    preferredQualifications: response.data.qualifications || [],
                    benefits: response.data.benefits || [],
                    teamInfo: '',
                    status: 'draft',
                    relocationSupport: false,
                    expirationDate: '',
                    postedDate: ''
                });

                toast.success(hasExistingData ? 'Job description updated successfully!' : 'Job description generated successfully!');
                // Clear the AI prompt after successful generation
                setAiPrompt('');
            } else {
                toast.error('Failed to generate job description');
            }
        } catch (error: any) {
            console.error('AI generation error:', error);

            // Handle specific error types
            if (error?.status === 429 || error?.error === 'QUOTA_EXCEEDED') {
                toast.error('AI Service Quota Exceeded', {
                    description: 'The AI service has reached its usage limit. Please try again in 5 minutes, or contact support to upgrade.',
                    duration: 8000,
                });
            } else if (error?.status === 503 || error?.error === 'SERVICE_UNAVAILABLE') {
                toast.error('AI Service Unavailable', {
                    description: 'The AI service is temporarily down. Please try again later.',
                    duration: 5000,
                });
            } else {
                toast.error(error?.message || 'Failed to generate job description. Please try again.');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSaveJob = () => {
        if (!formData.title || !formData.department || !formData.location) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (editingJob) {
            // Update existing job
            updateJob(editingJob.id, formData);
            toast.success('Job updated successfully');
        } else {
            // Add new job
            addJob(formData as Omit<Job, 'id'>);
            toast.success('Job posted successfully');
        }

        setShowJobForm(false);
        setEditingJob(null);
    };

    const handleDeleteJob = (job: Job) => {
        setDeleteModal({
            isOpen: true,
            itemId: job.id,
            itemName: job.title,
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));
        try {
            await careersAPI.delete(deleteModal.itemId);
            if (isSuperAdmin) {
                // Super admin: item is permanently deleted, remove from list
                deleteJob(deleteModal.itemId);
            } else {
                // Regular admin: item is soft-deleted, update visibility in local state
                // The job context will refresh, but we also update local state
                await refreshJobs();
            }
            toast.success(isSuperAdmin ? 'Career posting permanently deleted' : 'Career posting deleted successfully');
            setDeleteModal({
                isOpen: false,
                itemId: '',
                itemName: '',
                isDeleting: false
            });
        } catch (error) {
            toast.error('Failed to delete career posting');
            setDeleteModal(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleRestore = async (jobId: string) => {
        try {
            await careersAPI.update(jobId, { visibility: 'visible' } as any);
            await refreshJobs();
            toast.success('Career posting restored successfully');
        } catch (error) {
            console.error('Error restoring career:', error);
            toast.error('Failed to restore career posting');
        }
    };

    const handleStatusToggle = (id: string) => {
        const job = jobs.find(j => j.id === id);
        if (job) {
            updateJob(id, { 
                status: (job.status === 'active' ? 'inactive' : 'active') as Job['status'] 
            });
            toast.success('Job status updated');
        }
    };

    const addRequirement = () => {
        if (requirementInput.trim()) {
            setFormData({
                ...formData,
                requirements: [...(formData.requirements || []), requirementInput.trim()]
            });
            setRequirementInput('');
        }
    };

    const addResponsibility = () => {
        if (responsibilityInput.trim()) {
            setFormData({
                ...formData,
                responsibilities: [...(formData.responsibilities || []), responsibilityInput.trim()]
            });
            setResponsibilityInput('');
        }
    };

    const addBenefit = () => {
        if (benefitInput.trim()) {
            setFormData({
                ...formData,
                benefits: [...(formData.benefits || []), benefitInput.trim()]
            });
            setBenefitInput('');
        }
    };

    const addPreferredQualification = () => {
        if (preferredQualificationInput.trim()) {
            setFormData({
                ...formData,
                preferredQualifications: [...(formData.preferredQualifications || []), preferredQualificationInput.trim()]
            });
            setPreferredQualificationInput('');
        }
    };

    const removeItem = (type: 'requirements' | 'responsibilities' | 'benefits' | 'preferredQualifications', index: number) => {
        setFormData({
            ...formData,
            [type]: formData[type]?.filter((_, i) => i !== index)
        });
    };

    const updateItem = (type: 'requirements' | 'responsibilities' | 'benefits' | 'preferredQualifications', index: number, value: string) => {
        const updatedArray = [...(formData[type] || [])];
        updatedArray[index] = value;
        setFormData({
            ...formData,
            [type]: updatedArray
        });
    };

    const filteredJobs = useMemo(() => {
        return jobs.filter(job => {
            const matchesSearch = 
                job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.description.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
            const matchesDepartment = departmentFilter === 'all' || job.department === departmentFilter;
            
            return matchesSearch && matchesStatus && matchesDepartment;
        });
    }, [jobs, searchTerm, statusFilter, departmentFilter]);

    const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
    const paginatedJobs = filteredJobs.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const departments = useMemo(() => [...new Set(jobs.map(j => j.department))], [jobs]);

    const statusColors = {
        active: 'bg-green-500/20 text-green-400 border-green-500/30',
        inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };

    const visibilityColors = {
        visible: '',
        hidden: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    return (
        <AdminLayout>
            <AdminTopBar
                title="Career Postings"
                description="Manage job openings and career opportunities"
                actionButton={{
                    label: 'Add New Job',
                    icon: <Plus className="w-4 h-4 mr-2" />,
                    onClick: handleAddJob
                }}
            />

            <div className="p-8 space-y-6">
                <SearchAndFilters
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder="Search by title, department, location, or description..."
                    filters={[
                        {
                            value: statusFilter,
                            onChange: setStatusFilter,
                            options: [
                                { value: 'all', label: 'All Status' },
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                                { value: 'draft', label: 'Draft' }
                            ],
                            label: 'Status Filter'
                        },
                        {
                            value: departmentFilter,
                            onChange: setDepartmentFilter,
                            options: [
                                { value: 'all', label: 'All Departments' },
                                ...departments.map(dept => ({ value: dept, label: dept }))
                            ],
                            label: 'Department Filter'
                        }
                    ]}
                />

                {/* Jobs List */}
                <div className="space-y-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center space-y-3">
                                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                                <p className="text-muted-foreground">Loading jobs from database...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                    <div className="flex items-center justify-between">
                        <h3 className="text-foreground">
                            Job Openings ({filteredJobs.length})
                        </h3>
                        
                        {/* View Mode Toggle */}
                        <div className="flex items-center gap-2 glass rounded-lg p-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setViewMode('grid')}
                                className={`transition-all duration-300 ${
                                    viewMode === 'grid' 
                                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                                        : 'text-muted-foreground hover:text-foreground hover:bg-transparent'
                                }`}
                                title="Grid View"
                            >
                                <Grid3x3 className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setViewMode('list')}
                                className={`transition-all duration-300 ${
                                    viewMode === 'list' 
                                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                                        : 'text-muted-foreground hover:text-foreground hover:bg-transparent'
                                }`}
                                title="List View"
                            >
                                <List className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    <div className={viewMode === 'grid' ? "grid grid-cols-1 lg:grid-cols-2 gap-4" : "grid grid-cols-1 gap-4"}>
                        {paginatedJobs.map((job) => {
                            const isExpanded = expandedJobId === job.id;
                            
                            // Grid View - Compact Card
                            if (viewMode === 'grid') {
                                return (
                                    <motion.div
                                        key={job.id}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="glass-strong rounded-xl border border-primary/30 overflow-hidden hover:border-primary/50 transition-all duration-300"
                                    >
                                        <div className="p-6">
                                            {/* Header with Title and Status */}
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-foreground font-semibold text-lg mb-2 truncate">{job.title}</h4>
                                                    <div className="flex items-center gap-2 flex-wrap mb-3">
                                                        <Badge className={`text-xs ${statusColors[job.status || 'draft']}`}>
                                                            {job.status || 'draft'}
                                                        </Badge>
                                                        {job.visibility === 'hidden' && (
                                                            <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                                Deleted
                                                            </Badge>
                                                        )}
                                                        {job.expirationDate && (
                                                            <JobExpirationCountdown 
                                                                expirationDate={job.expirationDate}
                                                                showIcon={false}
                                                                className="text-xs"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Badges */}
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                <Badge variant="outline" className="border-primary/50 text-primary text-xs">
                                                    {job.department}
                                                </Badge>
                                                <Badge variant="outline" className="border-accent/50 text-accent text-xs">
                                                    {job.type}
                                                </Badge>
                                                {job.workMode && (
                                                    <Badge variant="outline" className="border-secondary/50 text-secondary text-xs">
                                                        {job.workMode}
                                                    </Badge>
                                                )}
                                            </div>

                                            {/* Description */}
                                            <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                                                {job.description}
                                            </p>

                                            {/* Info Grid */}
                                            <div className="grid grid-cols-2 gap-2 mb-4 text-xs text-muted-foreground">
                                                <div className="flex items-center gap-1">
                                                    <MapPin className="w-3 h-3 shrink-0" />
                                                    <span className="truncate">{job.location}</span>
                                                </div>
                                                {job.salary && (
                                                    <div className="flex items-center gap-1">
                                                        <DollarSign className="w-3 h-3 shrink-0" />
                                                        <span className="truncate">{job.salary}</span>
                                                    </div>
                                                )}
                                                {job.applicants !== undefined && (
                                                    <div className="flex items-center gap-1">
                                                        <Users className="w-3 h-3 shrink-0" />
                                                        <span>{job.applicants} applicants</span>
                                                    </div>
                                                )}
                                                {job.postedDate && (
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3 shrink-0" />
                                                        <span className="truncate">Posted {formatPostedDate(job.postedDate)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="space-y-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleViewMore(job)}
                                                    className="w-full text-accent border-accent/50 hover:bg-accent/10 hover-lift"
                                                >
                                                    <Eye className="w-3 h-3 mr-2" />
                                                    View More
                                                </Button>
                                                <div className="flex flex-wrap gap-2 pt-2 border-t border-primary/20">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleStatusToggle(job.id)}
                                                        className={`flex-1 ${job.status === 'active' ? 'text-green-400 border-green-400/30' : 'text-gray-400 border-gray-400/30'}`}
                                                    >
                                                        {job.status === 'active' ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                                                        {job.status === 'active' ? 'Active' : 'Inactive'}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleEditJob(job)}
                                                        className="flex-1 text-primary border-primary/30"
                                                    >
                                                        <Edit className="w-3 h-3 mr-1" />
                                                        Edit
                                                    </Button>
                                                    {isSuperAdmin && job.visibility === 'hidden' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleRestore(job.id)}
                                                            className="text-green-400 border-green-400/30 hover:bg-green-400/10"
                                                            title="Restore"
                                                        >
                                                            <RotateCcw className="w-3 h-3" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleDeleteJob(job)}
                                                        className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                                                        title={isSuperAdmin ? "Permanent Delete" : "Delete"}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            }
                            
                            // List View - Expandable Card (Original)
                            return (
                                <motion.div
                                    key={job.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="glass-strong rounded-xl border border-primary/30 overflow-hidden"
                                >
                                    {/* Job Header */}
                                    <div className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                    <h4 className="text-foreground text-xl">{job.title}</h4>
                                                    <Badge className={`text-xs ${statusColors[job.status || 'draft']}`}>
                                                        {job.status || 'draft'}
                                                    </Badge>
                                                    {job.visibility === 'hidden' && (
                                                        <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                            Deleted
                                                        </Badge>
                                                    )}
                                                    {job.expirationDate && (
                                                        <JobExpirationCountdown 
                                                            expirationDate={job.expirationDate}
                                                            showIcon={false}
                                                            className="text-xs"
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mb-3 flex-wrap">
                                                    <Badge variant="outline" className="border-primary/50 text-primary">
                                                        {job.department}
                                                    </Badge>
                                                    <Badge variant="outline" className="border-accent/50 text-accent">
                                                        {job.type}
                                                    </Badge>
                                                    {job.workMode && (
                                                        <Badge variant="outline" className="border-secondary/50 text-secondary">
                                                            {job.workMode}
                                                        </Badge>
                                                    )}
                                                    {job.experience && (
                                                        <Badge variant="outline" className="border-muted-foreground/50 text-muted-foreground">
                                                            {job.experience}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                                    {job.description}
                                                </p>
                                                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                                    <div className="flex items-center gap-1">
                                                        <MapPin className="w-4 h-4" />
                                                        <span>{job.location}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-4 h-4" />
                                                        <span>{job.type}</span>
                                                    </div>
                                                    {job.salary && (
                                                        <div className="flex items-center gap-1">
                                                            <DollarSign className="w-4 h-4" />
                                                            <span>{job.salary}</span>
                                                        </div>
                                                    )}
                                                    {job.applicants !== undefined && (
                                                        <div className="flex items-center gap-1">
                                                            <Users className="w-4 h-4" />
                                                            <span>{job.applicants} applicants</span>
                                                        </div>
                                                    )}
                                                    {job.postedDate && (
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="w-4 h-4" />
                                                            <span>Posted {formatPostedDate(job.postedDate)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2 ml-4">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => toggleExpanded(job.id)}
                                                    className="text-foreground hover:text-primary"
                                                    title={isExpanded ? "Collapse" : "Expand"}
                                                >
                                                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleStatusToggle(job.id)}
                                                    className={job.status === 'active' ? 'text-green-400' : 'text-gray-400'}
                                                    title={job.status === 'active' ? 'Set Inactive' : 'Set Active'}
                                                >
                                                    {job.status === 'active' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEditJob(job)}
                                                    className="text-primary hover:text-primary/80"
                                                    title="Edit Job"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                {job.applicationLink && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => window.open(job.applicationLink, '_blank')}
                                                        className="text-accent hover:text-accent/80"
                                                        title="View Application Link"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                {isSuperAdmin && job.visibility === 'hidden' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleRestore(job.id)}
                                                        className="text-green-400 hover:text-green-300"
                                                        title="Restore Job"
                                                    >
                                                        <RotateCcw className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDeleteJob(job)}
                                                    className="text-red-400 hover:text-red-300"
                                                    title={isSuperAdmin ? "Permanent Delete" : "Delete"}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="border-t border-primary/30"
                                            >
                                                <div className="p-6 space-y-6">
                                                    {/* Job Summary */}
                                                    {job.jobSummary && (
                                                        <div>
                                                            <h5 className="text-foreground font-semibold mb-2">Job Summary</h5>
                                                            <p className="text-muted-foreground text-sm">{job.jobSummary}</p>
                                                        </div>
                                                    )}

                                                    {/* Responsibilities */}
                                                    {job.responsibilities && job.responsibilities.length > 0 && (
                                                        <div>
                                                            <h5 className="text-foreground font-semibold mb-2">Key Responsibilities</h5>
                                                            <ul className="list-disc list-inside space-y-1 text-muted-foreground text-sm">
                                                                {job.responsibilities.map((resp, index) => (
                                                                    <li key={index}>{resp}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Requirements */}
                                                    {job.requirements && job.requirements.length > 0 && (
                                                        <div>
                                                            <h5 className="text-foreground font-semibold mb-2">Requirements</h5>
                                                            <ul className="list-disc list-inside space-y-1 text-muted-foreground text-sm">
                                                                {job.requirements.map((req, index) => (
                                                                    <li key={index}>{req}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Preferred Qualifications */}
                                                    {job.preferredQualifications && job.preferredQualifications.length > 0 && (
                                                        <div>
                                                            <h5 className="text-foreground font-semibold mb-2">Preferred Qualifications</h5>
                                                            <ul className="list-disc list-inside space-y-1 text-muted-foreground text-sm">
                                                                {job.preferredQualifications.map((qual, index) => (
                                                                    <li key={index}>{qual}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Benefits */}
                                                    {job.benefits && job.benefits.length > 0 && (
                                                        <div>
                                                            <h5 className="text-foreground font-semibold mb-2">Benefits</h5>
                                                            <div className="flex flex-wrap gap-2">
                                                                {job.benefits.map((benefit, index) => (
                                                                    <Badge 
                                                                        key={index} 
                                                                        variant="outline" 
                                                                        className="border-accent/50 text-accent"
                                                                    >
                                                                        {benefit}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Team Info */}
                                                    {job.teamInfo && (
                                                        <div>
                                                            <h5 className="text-foreground font-semibold mb-2">About the Team</h5>
                                                            <p className="text-muted-foreground text-sm">{job.teamInfo}</p>
                                                        </div>
                                                    )}

                                                    {/* Additional Info */}
                                                    <div className="flex items-center gap-4 text-sm flex-wrap">
                                                        {job.relocationSupport && (
                                                            <Badge variant="outline" className="border-primary/50 text-primary">
                                                                Relocation Support Available
                                                            </Badge>
                                                        )}
                                                        {job.applicationLink && (
                                                            <a 
                                                                href={job.applicationLink} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="text-primary hover:text-primary/80 flex items-center gap-1"
                                                            >
                                                                View Application
                                                                <ExternalLink className="w-3 h-3" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                        {filteredJobs.length === 0 && (
                            <EmptyState
                                icon={Briefcase}
                                title="No jobs found"
                                description="Try adjusting your search or filters"
                            />
                        )}
                    </div>
                    {filteredJobs.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            itemsPerPage={itemsPerPage}
                            totalItems={filteredJobs.length}
                        />
                    )}
                    </>
                    )}
                </div>

                <BackLink to="/admin" label="Back to Dashboard" />
            </div>

            {/* Job Form Modal */}
            <AnimatePresence>
                {showJobForm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowJobForm(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-strong rounded-2xl border border-primary/30 p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-foreground text-2xl">
                                    {editingJob ? 'Edit Job' : 'Add New Job'}
                                </h2>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowJobForm(false)}
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            <div className="space-y-6">
                                {/* AI Job Generation */}
                                {!editingJob && (
                                    <div className="bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 rounded-xl p-6 border border-primary/20">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Sparkles className="w-5 h-5 text-primary" />
                                            <h3 className="text-lg font-semibold text-foreground">AI-Powered Job Generation</h3>
                                        </div>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            {formData.title ? 
                                                "Modify the existing job description with additional requirements or changes:" : 
                                                "Describe the position you want to create, and AI will generate a complete job description:"
                                            }
                                        </p>
                                        <div className="space-y-3">
                                            <div className="space-y-2">
                                                <Label htmlFor="aiPrompt" className="text-foreground">
                                                    {formData.title ? "Modification Prompt" : "Job Description Prompt"}
                                                </Label>
                                                <textarea
                                                    id="aiPrompt"
                                                    value={aiPrompt}
                                                    onChange={(e) => setAiPrompt(e.target.value)}
                                                    className="w-full px-4 py-3 rounded-lg glass border border-border/30 text-foreground bg-background min-h-[100px] resize-none"
                                                    placeholder={
                                                        formData.title ? 
                                                        "e.g., Add React and TypeScript requirements, change location to Bangalore, increase salary range..." :
                                                        "e.g., Senior AI Engineer with 5+ years experience in machine learning and Python, remote work, competitive salary..."
                                                    }
                                                    disabled={isGenerating}
                                                />
                                            </div>
                                            <Button
                                                onClick={handleAIGenerate}
                                                disabled={isGenerating || !aiPrompt.trim()}
                                                className="w-full sm:w-auto"
                                            >
                                                {isGenerating ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles className="w-4 h-4 mr-2" />
                                                        {formData.title ? "Update Job Description" : "Generate Job Description"}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Basic Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="title">Job Title *</Label>
                                        <Input
                                            id="title"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            className="glass border-border/30"
                                            placeholder="e.g., Senior AI Engineer"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="department">Department *</Label>
                                        <Input
                                            id="department"
                                            value={formData.department}
                                            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                            className="glass border-border/30"
                                            placeholder="e.g., Engineering, Marketing, Sales"
                                            list="department-suggestions"
                                        />
                                        <datalist id="department-suggestions">
                                            {DEPARTMENTS.map((dept) => (
                                                <option key={dept} value={dept} />
                                            ))}
                                        </datalist>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="location">Location *</Label>
                                        <Input
                                            id="location"
                                            value={formData.location}
                                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                            className="glass border-border/30"
                                            placeholder="e.g., Bangalore, Remote, New York"
                                            list="location-suggestions"
                                        />
                                        <datalist id="location-suggestions">
                                            {LOCATIONS.map((location) => (
                                                <option key={location} value={location} />
                                            ))}
                                        </datalist>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="workMode">Work Mode</Label>
                                        <Input
                                            id="workMode"
                                            value={formData.workMode || ''}
                                            onChange={(e) => setFormData({ ...formData, workMode: e.target.value })}
                                            className="glass border-border/30"
                                            placeholder="e.g., Hybrid, Remote, On-site"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="type">Job Type</Label>
                                        <select
                                            id="type"
                                            value={formData.type || 'Full-time'}
                                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                            className="w-full px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background"
                                        >
                                            <option value="Full-time">Full-time</option>
                                            <option value="Part-time">Part-time</option>
                                            <option value="Contract">Contract</option>
                                            <option value="Internship">Internship</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="experience">Experience Required</Label>
                                        <Input
                                            id="experience"
                                            value={formData.experience || ''}
                                            onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                                            className="glass border-border/30"
                                            placeholder="e.g., 3+ years"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="salary">Salary Range</Label>
                                        <Input
                                            id="salary"
                                            value={formData.salary || ''}
                                            onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                                            className="glass border-border/30"
                                            placeholder="e.g., ₹10 LPA – ₹15 LPA"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="expirationDate">Expiration Date</Label>
                                        <Input
                                            id="expirationDate"
                                            type="date"
                                            value={formData.expirationDate ? formData.expirationDate.split('T')[0] : ''}
                                            onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                                            className="glass border-border/30"
                                        />
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="space-y-2">
                                    <Label htmlFor="description">Job Description</Label>
                                    <textarea
                                        id="description"
                                        value={formData.description || ''}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background min-h-20"
                                        placeholder="Brief description of the role..."
                                    />
                                </div>

                                {/* Job Summary */}
                                <div className="space-y-2">
                                    <Label htmlFor="jobSummary">Job Summary</Label>
                                    <textarea
                                        id="jobSummary"
                                        value={formData.jobSummary || ''}
                                        onChange={(e) => setFormData({ ...formData, jobSummary: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background min-h-[100px]"
                                        placeholder="Detailed summary about the position..."
                                    />
                                </div>

                                {/* Responsibilities */}
                                <div className="space-y-2">
                                    <Label>Key Responsibilities</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={responsibilityInput}
                                            onChange={(e) => setResponsibilityInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addResponsibility())}
                                            className="glass border-border/30"
                                            placeholder="Add a responsibility and press Enter"
                                        />
                                        <Button type="button" onClick={addResponsibility} size="icon">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-col gap-2 mt-2">
                                        {formData.responsibilities?.map((resp, index) => (
                                            <div key={index} className="flex items-start gap-2 p-2 glass rounded border border-blue-500/30">
                                                <button
                                                    onClick={() => removeItem('responsibilities', index)}
                                                    className="shrink-0 hover:text-red-400 transition-colors mt-0.5"
                                                    title="Remove"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                <input
                                                    type="text"
                                                    value={resp}
                                                    onChange={(e) => updateItem('responsibilities', index, e.target.value)}
                                                    className="flex-1 bg-transparent border-none outline-none text-blue-400 text-sm"
                                                    placeholder="Responsibility..."
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Requirements */}
                                <div className="space-y-2">
                                    <Label>Requirements</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={requirementInput}
                                            onChange={(e) => setRequirementInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                                            className="glass border-border/30"
                                            placeholder="Add a requirement and press Enter"
                                        />
                                        <Button type="button" onClick={addRequirement} size="icon">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-col gap-2 mt-2">
                                        {formData.requirements?.map((req, index) => (
                                            <div key={index} className="flex items-start gap-2 p-2 glass rounded border border-primary/30">
                                                <button
                                                    onClick={() => removeItem('requirements', index)}
                                                    className="shrink-0 hover:text-red-400 transition-colors mt-0.5"
                                                    title="Remove"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                <input
                                                    type="text"
                                                    value={req}
                                                    onChange={(e) => updateItem('requirements', index, e.target.value)}
                                                    className="flex-1 bg-transparent border-none outline-none text-primary text-sm"
                                                    placeholder="Requirement..."
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Preferred Qualifications */}
                                <div className="space-y-2">
                                    <Label>Preferred Qualifications</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={preferredQualificationInput}
                                            onChange={(e) => setPreferredQualificationInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPreferredQualification())}
                                            className="glass border-border/30"
                                            placeholder="Add a preferred qualification and press Enter"
                                        />
                                        <Button type="button" onClick={addPreferredQualification} size="icon">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-col gap-2 mt-2">
                                        {formData.preferredQualifications?.map((qual, index) => (
                                            <div key={index} className="flex items-start gap-2 p-2 glass rounded border border-purple-500/30">
                                                <button
                                                    onClick={() => removeItem('preferredQualifications', index)}
                                                    className="shrink-0 hover:text-red-400 transition-colors mt-0.5"
                                                    title="Remove"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                <input
                                                    type="text"
                                                    value={qual}
                                                    onChange={(e) => updateItem('preferredQualifications', index, e.target.value)}
                                                    className="flex-1 bg-transparent border-none outline-none text-purple-400 text-sm"
                                                    placeholder="Qualification..."
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Benefits */}
                                <div className="space-y-2">
                                    <Label>Benefits</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={benefitInput}
                                            onChange={(e) => setBenefitInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                                            className="glass border-border/30"
                                            placeholder="Add a benefit and press Enter"
                                        />
                                        <Button type="button" onClick={addBenefit} size="icon">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-col gap-2 mt-2">
                                        {formData.benefits?.map((benefit, index) => (
                                            <div key={index} className="flex items-start gap-2 p-2 glass rounded border border-green-500/30">
                                                <button
                                                    onClick={() => removeItem('benefits', index)}
                                                    className="shrink-0 hover:text-red-400 transition-colors mt-0.5"
                                                    title="Remove"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                <input
                                                    type="text"
                                                    value={benefit}
                                                    onChange={(e) => updateItem('benefits', index, e.target.value)}
                                                    className="flex-1 bg-transparent border-none outline-none text-green-400 text-sm"
                                                    placeholder="Benefit..."
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Team Info */}
                                <div className="space-y-2">
                                    <Label htmlFor="teamInfo">About the Team</Label>
                                    <textarea
                                        id="teamInfo"
                                        value={formData.teamInfo || ''}
                                        onChange={(e) => setFormData({ ...formData, teamInfo: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background min-h-20"
                                        placeholder="Tell candidates about the team they'll be joining..."
                                    />
                                </div>

                                {/* Additional Options */}
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="relocationSupport"
                                            checked={formData.relocationSupport || false}
                                            onChange={(e) => setFormData({ ...formData, relocationSupport: e.target.checked })}
                                            className="w-4 h-4 rounded border-border/30"
                                        />
                                        <Label htmlFor="relocationSupport" className="cursor-pointer">
                                            Relocation Support Available
                                        </Label>
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    <select
                                        id="status"
                                        value={formData.status || 'draft'}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value as Job['status'] })}
                                        className="w-full px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background"
                                    >
                                        <option value="draft">Draft</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-4 pt-4">
                                    <Button
                                        onClick={handleSaveJob}
                                        className="flex-1 bg-primary hover:bg-primary/90"
                                    >
                                        <Save className="w-4 h-4 mr-2" />
                                        {editingJob ? 'Update Job' : 'Post Job'}
                                    </Button>
                                    <Button
                                        onClick={() => setShowJobForm(false)}
                                        variant="outline"
                                        className="flex-1"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Job Detail Modal */}
            <AnimatePresence>
                {showDetailModal && selectedJob && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowDetailModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-strong rounded-2xl border border-primary/30 w-full max-w-4xl max-h-[90vh] overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="p-6 border-b border-primary/30 sticky top-0 glass-strong z-10">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h3 className="text-foreground text-2xl font-bold mb-2">{selectedJob.title}</h3>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge className={`${statusColors[selectedJob.status || 'draft']}`}>
                                                {selectedJob.status || 'draft'}
                                            </Badge>
                                            {selectedJob.visibility === 'hidden' && (
                                                <Badge className={`${visibilityColors.hidden}`}>
                                                    Deleted
                                                </Badge>
                                            )}
                                            <Badge variant="outline" className="border-primary/50 text-primary">
                                                {selectedJob.department}
                                            </Badge>
                                            <Badge variant="outline" className="border-accent/50 text-accent">
                                                {selectedJob.type}
                                            </Badge>
                                            {selectedJob.workMode && (
                                                <Badge variant="outline" className="border-secondary/50 text-secondary">
                                                    {selectedJob.workMode}
                                                </Badge>
                                            )}
                                            {selectedJob.expirationDate && (
                                                <JobExpirationCountdown 
                                                    expirationDate={selectedJob.expirationDate}
                                                    showIcon={false}
                                                    className="text-xs"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setShowDetailModal(false)}
                                        className="text-foreground hover:text-primary"
                                    >
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] space-y-6">
                                {/* Basic Info */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="flex items-center gap-2 p-3 glass rounded-lg border border-primary/20">
                                        <MapPin className="w-5 h-5 text-primary" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Location</p>
                                            <p className="text-sm text-foreground font-medium">{selectedJob.location}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 p-3 glass rounded-lg border border-primary/20">
                                        <Clock className="w-5 h-5 text-accent" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Employment Type</p>
                                            <p className="text-sm text-foreground font-medium">{selectedJob.type}</p>
                                        </div>
                                    </div>
                                    {selectedJob.salary && (
                                        <div className="flex items-center gap-2 p-3 glass rounded-lg border border-primary/20">
                                            <DollarSign className="w-5 h-5 text-secondary" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Salary</p>
                                                <p className="text-sm text-foreground font-medium">{selectedJob.salary}</p>
                                            </div>
                                        </div>
                                    )}
                                    {selectedJob.experience && (
                                        <div className="flex items-center gap-2 p-3 glass rounded-lg border border-primary/20">
                                            <Briefcase className="w-5 h-5 text-accent" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Experience</p>
                                                <p className="text-sm text-foreground font-medium">{selectedJob.experience}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-2 gap-4">
                                    {selectedJob.applicants !== undefined && (
                                        <div className="p-4 glass rounded-lg border border-primary/20 text-center">
                                            <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                                            <p className="text-2xl font-bold text-foreground text-center">{selectedJob.applicants}</p>
                                            <p className="text-xs text-muted-foreground text-center">Applicants</p>
                                        </div>
                                    )}
                                    {selectedJob.postedDate && (
                                        <div className="p-4 glass rounded-lg border border-primary/20 text-center">
                                            <Calendar className="w-6 h-6 text-accent mx-auto mb-2" />
                                            <p className="text-sm font-bold text-foreground text-center">{formatPostedDate(selectedJob.postedDate)}</p>
                                            <p className="text-xs text-muted-foreground text-center">Posted Date</p>
                                        </div>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="glass-strong p-5 rounded-lg border border-primary/20">
                                    <h4 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                                        <div className="w-1 h-5 bg-primary rounded-full"></div>
                                        Job Description
                                    </h4>
                                    <p className="text-muted-foreground text-sm leading-relaxed">{selectedJob.description}</p>
                                </div>

                                {/* Job Summary */}
                                {selectedJob.jobSummary && (
                                    <div className="glass-strong p-5 rounded-lg border border-primary/20">
                                        <h4 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                                            <div className="w-1 h-5 bg-accent rounded-full"></div>
                                            Job Summary
                                        </h4>
                                        <p className="text-muted-foreground text-sm leading-relaxed">{selectedJob.jobSummary}</p>
                                    </div>
                                )}

                                {/* Responsibilities */}
                                {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                                    <div className="glass-strong p-5 rounded-lg border border-primary/20">
                                        <h4 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                                            <div className="w-1 h-5 bg-secondary rounded-full"></div>
                                            Key Responsibilities
                                        </h4>
                                        <ul className="space-y-2">
                                            {selectedJob.responsibilities.map((resp, index) => (
                                                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                    <span className="text-primary mt-1">•</span>
                                                    <span>{resp}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Requirements */}
                                {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                                    <div className="glass-strong p-5 rounded-lg border border-primary/20">
                                        <h4 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                                            <div className="w-1 h-5 bg-primary rounded-full"></div>
                                            Requirements
                                        </h4>
                                        <ul className="space-y-2">
                                            {selectedJob.requirements.map((req, index) => (
                                                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                    <span className="text-accent mt-1">•</span>
                                                    <span>{req}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Preferred Qualifications */}
                                {selectedJob.preferredQualifications && selectedJob.preferredQualifications.length > 0 && (
                                    <div className="glass-strong p-5 rounded-lg border border-primary/20">
                                        <h4 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                                            <div className="w-1 h-5 bg-accent rounded-full"></div>
                                            Preferred Qualifications
                                        </h4>
                                        <ul className="space-y-2">
                                            {selectedJob.preferredQualifications.map((qual, index) => (
                                                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                    <span className="text-secondary mt-1">•</span>
                                                    <span>{qual}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Benefits */}
                                {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                                    <div className="glass-strong p-5 rounded-lg border border-primary/20">
                                        <h4 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                                            <div className="w-1 h-5 bg-secondary rounded-full"></div>
                                            Benefits & Perks
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedJob.benefits.map((benefit, index) => (
                                                <Badge 
                                                    key={index} 
                                                    variant="outline" 
                                                    className="border-accent/50 text-accent bg-accent/5 hover:bg-accent/10 transition-colors"
                                                >
                                                    {benefit}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Team Info */}
                                {selectedJob.teamInfo && (
                                    <div className="glass-strong p-5 rounded-lg border border-primary/20">
                                        <h4 className="text-foreground font-semibold mb-3 flex items-center gap-2">
                                            <div className="w-1 h-5 bg-primary rounded-full"></div>
                                            About the Team
                                        </h4>
                                        <p className="text-muted-foreground text-sm leading-relaxed">{selectedJob.teamInfo}</p>
                                    </div>
                                )}

                                {/* Relocation Support */}
                                {selectedJob.relocationSupport && (
                                    <div className="glass-strong p-4 rounded-lg border border-accent/30 bg-accent/5">
                                        <div className="flex items-center gap-2 text-accent">
                                            <MapPin className="w-5 h-5" />
                                            <p className="font-medium">Relocation support available</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="p-6 border-t border-primary/30 glass-strong sticky bottom-0">
                                <div className="flex gap-3 flex-wrap">
                                    <Button
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            handleEditJob(selectedJob);
                                        }}
                                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground button-ripple hover-lift"
                                    >
                                        <Edit className="w-4 h-4 mr-2" />
                                        Edit Job
                                    </Button>
                                    {selectedJob.applicationLink && (
                                        <Button
                                            onClick={() => window.open(selectedJob.applicationLink, '_blank')}
                                            variant="outline"
                                            className="flex-1 border-accent/50 text-accent hover:bg-accent/10"
                                        >
                                            <ExternalLink className="w-4 h-4 mr-2" />
                                            Application Link
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => setShowDetailModal(false)}
                                        variant="outline"
                                        className="flex-1"
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => !deleteModal.isDeleting && setDeleteModal({ isOpen: false, itemId: '', itemName: '', isDeleting: false })}
                onConfirm={confirmDelete}
                title={isSuperAdmin ? "Permanently Delete Career Posting" : "Delete Career Posting"}
                message={isSuperAdmin ? "Are you sure you want to permanently delete this job posting?" : "Are you sure you want to delete this job posting?"}
                itemName={deleteModal.itemName}
                isDeleting={deleteModal.isDeleting}
                isPermanent={isSuperAdmin}
            />
        </AdminLayout>
    );
}
